import { sandboxNodeInventorySnapshotSchema } from "@arti/runtime-shared/sandboxInventory";
import { taskCallbackTargetSchema } from "@arti/runtime-shared/taskDispatch";
import z from "zod";

import {
  genericTaskMetadataResponseSchema,
  genericTaskPayloadResponseSchema,
} from "./shared";

export const nodeHealthDetailedSchema = z
  .object({
    boot_id: z.string(),
    capabilities: z.array(z.string()),
    encryption_fingerprint: z
      .string()
      .regex(/^[0-9a-f]{8}$/)
      .nullable(),
    generation_id: z.string().nullable(),
    owner_lease_epoch: z.number().int().nonnegative(),
    /**
     * Node app ids this node can forward owner-routed effects to: its
     * configured service routes plus itself. Optional so old nodes' health
     * responses still parse; absence means "self only" to the scheduler.
     */
    owner_route_node_ids: z.array(z.string()).optional(),
    rootfs_quota: z.object({
      capabilities: z.array(z.string()),
      mode: z.enum(["off", "required"]),
      reason: z.string().nullable(),
      status: z.enum(["disabled", "checking", "ready", "unhealthy"]),
    }),
    sandbox: sandboxNodeInventorySnapshotSchema,
    shutting_down: z.boolean(),
    status: z.enum(["ok", "degraded", "unhealthy"]),
  })
  .meta({ ref: "NodeHealthDetailed" });

// MARK: Server

const nodeRunRequestCallbackSchema = taskCallbackTargetSchema.meta({
  ref: "NodeRunRequestCallback",
});

const nodeRunRequestSourceControlRuntimeInsightSchema = z
  .object({
    name: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
  })
  .meta({ ref: "NodeRunRequestSourceControlRuntimeInsight" });

const nodeRunRequestSourceControlRepositorySchema = z
  .object({
    default_branch: z.string().min(1).nullable(),
    read_token: z.string().min(1).optional(),
    remote_slug: z.string().min(1),
    repository_id: z.string().min(1),
    runtime_insights: z.array(nodeRunRequestSourceControlRuntimeInsightSchema),
  })
  .meta({ ref: "NodeRunRequestSourceControlRepository" });

const nodeRunRequestSourceControlSchema = z
  .object({
    base_url: z.string().min(1).nullable(),
    credential_id: z.string().min(1),
    default_org: z.string().min(1).nullable(),
    default_project: z.string().min(1).nullable(),
    github_app_installation_id: z.number().int().positive().nullable(),
    provider_id: z.string().min(1),
    provider_type: z.enum(["github"]),
    repositories: z.array(nodeRunRequestSourceControlRepositorySchema),
    token: z.string().min(1).optional(),
  })
  .meta({ ref: "NodeRunRequestSourceControl" });

export const nodeRunRequestSchema = z
  .object({
    callback: nodeRunRequestCallbackSchema.nullable().optional(),
    /**
     * Stage E dispatch-attempt binding (item 4b, D-rev19.2). OPTIONAL on
     * purpose: an old-orchestrator dispatch that omits it must still admit
     * cleanly. A node that receives it echoes it on EVERY callback for this
     * run — terminal and progress alike. One wire name throughout:
     * `dispatch_attempt_id`.
     *
     * Stage C (a future release train, NOT this batch) makes it required and
     * deletes the orchestrator's attempt-less fallback. Its preconditions:
     * fleet homogeneity, zero non-terminal legacy attempts, and zero
     * incomplete legacy terminal settlements.
     */
    dispatch_attempt_id: z.string().min(1).nullable().optional(),
    metadata: genericTaskMetadataResponseSchema.optional(),
    organization_id: z.string().min(1).nullable().optional(),
    payload: genericTaskPayloadResponseSchema.optional(),
    source_control: nodeRunRequestSourceControlSchema.nullable().optional(),
    source_control_provider_id: z.string().min(1).nullable().optional(),
    task_id: z.string().min(1),
  })
  .meta({ ref: "NodeRunRequest" });

export const nodeRunResponseSchema = z
  .object({ node_id: z.string(), status: z.string(), task_id: z.string() })
  .meta({ ref: "NodeRunResponse" });

export const nodeCancelRequestSchema = z
  .object({
    reason: z.string().optional(),
    status: z.literal("cancelled").optional(),
    task_id: z.string().min(1),
  })
  .meta({ ref: "NodeCancelRequest" });

export const nodeCancelResponseSchema = z
  .object({ status: z.string(), task_id: z.string() })
  .meta({ ref: "NodeCancelResponse" });

export const nodeOrchestratorResetRequestSchema = z
  .object({ generation_id: z.string().min(1), reason: z.string().optional() })
  .meta({ ref: "NodeOrchestratorResetRequest" });

export const nodeOrchestratorResetResponseSchema = z
  .object({
    cancelled: z.array(z.string()),
    generation_id: z.string(),
    status: z.string(),
    still_running: z.array(z.string()),
  })
  .meta({ ref: "NodeOrchestratorResetResponse" });

export const nodeChannelEnvironmentParamSchema = z.object({
  id: z.string().min(1),
});

export const nodeChannelEnvironmentRepositoryParamSchema = z.object({
  id: z.string().min(1),
  repoAttachmentId: z.string().min(1),
});

export const nodeChannelEnvironmentControlRequestSchema = z
  .object({
    capacity_move: z.boolean().optional(),
    capacity_move_fence_generation: z.string().regex(/^\d+$/u).optional(),
    capacity_move_fence_owner: z.string().min(1).optional(),
    compute_resize_operation_id: z.string().trim().min(1).max(64).optional(),
    dormancy_expected_channel_archived_at: z.string().datetime().optional(),
    dormancy_expected_channel_created_at: z.string().datetime().optional(),
    force: z.boolean().optional(),
    idle_stop_expected_lifecycle_seq: z.number().int().nonnegative().optional(),
    idle_stop_last_activity_before: z
      .string()
      .datetime()
      .optional()
      .describe(
        "Deprecated rolling-version compatibility input. Current nodes accept but do not use this timestamp for idle eligibility.",
      ),
    inventory_id: z.string().min(1).optional(),
    organization_id: z.string().min(1),
    requested_by: z.string().min(1).optional(),
    reservation_id: z.string().min(1).optional(),
  })
  .refine(
    (body) =>
      (body.dormancy_expected_channel_archived_at === undefined) ===
      (body.dormancy_expected_channel_created_at === undefined),
    { message: "Dormancy channel fence timestamps must be supplied together." },
  )
  .refine(
    (body) =>
      (body.idle_stop_expected_lifecycle_seq === undefined) ===
      (body.idle_stop_last_activity_before === undefined),
    { message: "Idle-stop compatibility inputs must be supplied together." },
  )
  .meta({ ref: "NodeChannelEnvironmentControlRequest" });

export const nodeLostHostRecoveryCheckpointInspectRequestSchema = z
  .object({
    checkpoint_generation: z.number().int().positive(),
    checkpoint_ref: z.string().min(1),
    checkpoint_sequence: z.number().int().positive(),
    organization_id: z.string().min(1),
  })
  .meta({ ref: "NodeLostHostRecoveryCheckpointInspectRequest" });

export const nodeLostHostRecoveryCheckpointInspectResponseSchema = z
  .object({
    checkpoint_created_at: z.string().datetime(),
    checkpoint_id: z.string().min(1),
  })
  .meta({ ref: "NodeLostHostRecoveryCheckpointInspectResponse" });

export const nodeChannelEnvironmentRepositoryReconcileRequestSchema = z
  .object({
    expected_owner_node_id: z.string().min(1),
    organization_id: z.string().min(1),
    owner_epoch: z.number().int().nonnegative(),
  })
  .meta({ ref: "NodeChannelEnvironmentRepositoryReconcileRequest" });

/**
 * Absence acknowledgment is an explicit, caller-specific owner-node operation:
 * the requester identity is part of the request because it becomes part of the
 * durable terminal record.
 */
export const nodeChannelEnvironmentRepositoryAcknowledgeAbsentDetachRequestSchema =
  z
    .object({
      expected_owner_node_id: z.string().min(1),
      organization_id: z.string().min(1),
      owner_epoch: z.number().int().nonnegative(),
      requested_by: z.string().min(1),
    })
    .meta({
      ref: "NodeChannelEnvironmentRepositoryAcknowledgeAbsentDetachRequest",
    });

export const nodeChannelEnvironmentRepositoryAcknowledgeAbsentDetachResponseSchema =
  z
    .object({
      channel_environment_id: z.string(),
      lifecycle_seq: z.number().int().nonnegative(),
      repo_attachment_id: z.string(),
      status: z.literal("detached"),
    })
    .meta({
      ref: "NodeChannelEnvironmentRepositoryAcknowledgeAbsentDetachResponse",
    });

export const nodeChannelEnvironmentCapacityReplaceRequestSchema = z
  .object({
    expected_container_id: z.string().min(1),
    expected_owner_boot_id: z.string().min(1),
    expected_owner_epoch: z.number().int().nonnegative(),
    expected_owner_lease_epoch: z.number().int().nonnegative(),
    expected_owner_node_id: z.string().min(1),
    organization_id: z.string().min(1),
    target_boot_id: z.string().min(1),
    target_node_id: z.string().min(1),
    target_owner_lease_epoch: z.number().int().nonnegative(),
    target_service_url: z.string().url(),
  })
  .meta({ ref: "NodeChannelEnvironmentCapacityReplaceRequest" });

export const nodeChannelEnvironmentCapacityReplaceResponseSchema = z
  .object({ replaced: z.boolean() })
  .meta({ ref: "NodeChannelEnvironmentCapacityReplaceResponse" });

export const nodeChannelEnvironmentCapacityMoveSourceStopRequestSchema = z
  .object({
    expected_container_id: z.string().min(1),
    expected_lifecycle_seq: z.number().int().nonnegative(),
    expected_owner_boot_id: z.string().min(1),
    expected_owner_epoch: z.number().int().nonnegative(),
    expected_owner_lease_epoch: z.number().int().nonnegative(),
    expected_owner_node_id: z.string().min(1),
    operation_id: z.string().min(1),
    organization_id: z.string().min(1),
  })
  .meta({ ref: "NodeChannelEnvironmentCapacityMoveSourceStopRequest" });

export const nodeChannelEnvironmentCapacityMoveSourceStopResponseSchema = z
  .union([
    z.object({
      checkpointRef: z.string().nullable(),
      containerGone: z.literal(true),
      stopped: z.literal(true),
    }),
    z.object({ reason: z.string().min(1), stopped: z.literal(false) }),
    z.object({ outcome: z.literal("unknown") }),
  ])
  .meta({ ref: "NodeChannelEnvironmentCapacityMoveSourceStopResponse" });

export const nodeChannelEnvironmentCapacityMoveInspectRequestSchema = z
  .object({
    capacity_move_fence_generation: z.string().regex(/^\d+$/u).optional(),
    capacity_move_fence_owner: z.string().min(1).optional(),
    expected_container_id: z.string().min(1).optional(),
    expected_owner_boot_id: z.string().min(1).optional(),
    expected_owner_epoch: z.number().int().nonnegative().optional(),
    expected_owner_lease_epoch: z.number().int().nonnegative().optional(),
    expected_owner_node_id: z.string().min(1).optional(),
    inventory_id: z.string().min(1).optional(),
    organization_id: z.string().min(1),
    reservation_id: z.string().min(1).optional(),
  })
  .meta({ ref: "NodeChannelEnvironmentCapacityMoveInspectRequest" });

export const nodeChannelEnvironmentCapacityMoveCleanupStaleRequestSchema = z
  .object({
    capacity_move_fence_generation: z.string().regex(/^\d+$/u),
    capacity_move_fence_owner: z.string().min(1),
    expected_container_id: z.string().min(1).optional(),
    expected_owner_boot_id: z.string().min(1),
    expected_owner_epoch: z.number().int().nonnegative(),
    expected_owner_lease_epoch: z.number().int().nonnegative(),
    expected_owner_node_id: z.string().min(1),
    inventory_id: z.string().min(1),
    organization_id: z.string().min(1),
    reservation_id: z.string().min(1),
  })
  .meta({ ref: "NodeChannelEnvironmentCapacityMoveCleanupStaleRequest" });

export const nodeChannelEnvironmentCapacityMoveInspectResponseSchema = z
  .union([
    z.object({
      container_id: z.string().min(1),
      outcome: z.literal("running"),
    }),
    z.object({ outcome: z.literal("absent") }),
    z.object({ outcome: z.literal("unknown"), reason: z.string().min(1) }),
  ])
  .meta({ ref: "NodeChannelEnvironmentCapacityMoveInspectResponse" });

export const nodeChannelEnvironmentStopActorSpawnsRequestSchema = z
  .object({
    actor_instance_id: z.string().min(1),
    channel_environment_ids: z.array(z.string().min(1)).optional(),
    organization_id: z.string().min(1),
  })
  .meta({ ref: "NodeChannelEnvironmentStopActorSpawnsRequest" });

export const nodeChannelEnvironmentPreviewGrantRequestSchema = z
  .object({
    dashboard_error_url: z.string().url().nullable().optional(),
    organization_id: z.string().min(1),
    public_port: z.number().int().min(1).max(65_535),
    user_id: z.string().min(1),
  })
  .meta({ ref: "NodeChannelEnvironmentPreviewGrantRequest" });

export const nodeChannelEnvironmentControlResponseSchema = z
  .object({
    channel_environment_id: z.string(),
    container_id: z.string().nullable(),
    repository_refresh: z
      .object({
        autostash_commit_sha: z.string().nullable(),
        autostash_ref: z.string().nullable(),
        autostash_retained: z.boolean(),
        autostash_retained_reason: z
          .enum(["apply_conflict", "drop_failed", "merge_conflict"])
          .nullable(),
        autostash_short_id: z.string().nullable(),
        autostash_token: z.string().nullable(),
        clean_files: z.array(z.string()),
        conflicted_files: z.array(z.string()),
        force: z.boolean(),
        has_conflicts: z.boolean(),
        new_commit_sha: z.string(),
        old_commit_sha: z.string(),
        requested_by: z.string().nullable(),
      })
      .optional(),
    status: z.string(),
  })
  .meta({ ref: "NodeChannelEnvironmentControlResponse" });

const nodeChannelEnvironmentComputeCapacitySchema = z.object({
  cpuMilli: z.number().int().nonnegative(),
  liveEnvironments: z.number().int().nonnegative(),
  ramMb: z.number().int().nonnegative(),
});

const nodeChannelEnvironmentLifecycleDiagnosticSchema = z.object({
  message: z.string().min(1).optional(),
  operation_id: z.string().min(1).optional(),
  phase: z.string().min(1).optional(),
});

export const nodeChannelEnvironmentResumeUnavailableResponseSchema = z
  .object({
    available: nodeChannelEnvironmentComputeCapacitySchema,
    code: z.literal("sandbox_capacity_unavailable"),
    deficit: nodeChannelEnvironmentComputeCapacitySchema,
    reason: z.enum([
      "manifest_corrupt",
      "manifest_incompatible",
      "cluster_capacity_unavailable",
      "node_maintenance",
      "pinned_host_changed",
      "pinned_node_capability_mismatch",
      "pinned_node_compute_unavailable",
      "pinned_node_offline",
      "storage_unreachable",
    ]),
    requested: nodeChannelEnvironmentComputeCapacitySchema,
    retryable: z.boolean(),
    storageHeld: z.object({
      committedImageMb: z.number().int().nonnegative(),
      hibernateArchiveMb: z.number().int().nonnegative(),
      rootfsMb: z.number().int().nonnegative(),
      runtimeMb: z.number().int().nonnegative(),
      workspaceMb: z.number().int().nonnegative(),
    }),
    suggestedActions: z.array(
      z.enum(["contact_support", "downscale_cpu_ram", "retry"]),
    ),
    ...nodeChannelEnvironmentLifecycleDiagnosticSchema.shape,
  })
  .meta({ ref: "NodeChannelEnvironmentResumeUnavailableResponse" });

export const nodeChannelEnvironmentHibernationUnavailableResponseSchema = z
  .object({
    available: z.object({
      rootfsMb: z.number().int().nonnegative(),
      workspaceMb: z.number().int().nonnegative(),
    }),
    code: z.literal("sandbox_storage_unavailable"),
    reason: z.literal("hibernation_storage_capacity_unavailable").optional(),
    required: z.object({
      rootfsMb: z.number().int().nonnegative(),
      workspaceMb: z.number().int().nonnegative(),
    }),
    retryable: z.boolean().optional(),
    ...nodeChannelEnvironmentLifecycleDiagnosticSchema.shape,
  })
  .meta({ ref: "NodeChannelEnvironmentHibernationUnavailableResponse" });

export const nodeChannelEnvironmentLifecycleFailureResponseSchema = z
  .object({
    code: z.literal("channel_environment_lifecycle_failed"),
    message: z.string().min(1).optional(),
    operation_id: z.string().min(1).optional(),
    phase: z.string().min(1).optional(),
    reason: z.string().min(1).optional(),
    retryable: z.boolean().optional(),
  })
  .meta({ ref: "NodeChannelEnvironmentLifecycleFailureResponse" });

export const nodeChannelEnvironmentLifecycleErrorResponseSchema = z.union([
  nodeChannelEnvironmentHibernationUnavailableResponseSchema,
  nodeChannelEnvironmentLifecycleFailureResponseSchema,
  nodeChannelEnvironmentResumeUnavailableResponseSchema,
]);

export const nodeChannelEnvironmentCapacityReplaceStartPreEffectFailureResponseSchema =
  z
    .object({
      error: z.string().min(1),
      localEffects: z.literal("none"),
      message: z.string().min(1),
      safeToRollback: z.literal(true),
      started: z.literal(false),
    })
    .meta({
      ref: "NodeChannelEnvironmentCapacityReplaceStartPreEffectFailureResponse",
    });

export const nodeChannelEnvironmentCapacityReplaceStartUnsafeFailureResponseSchema =
  z
    .object({
      error: z.string().min(1),
      localEffects: z.literal("unknown"),
      message: z.string().min(1),
      safeToRollback: z.literal(false),
      started: z.literal(false),
    })
    .meta({
      ref: "NodeChannelEnvironmentCapacityReplaceStartUnsafeFailureResponse",
    });

export const nodeChannelEnvironmentStopActorSpawnsResponseSchema = z
  .object({ stopped_count: z.number().int().nonnegative() })
  .meta({ ref: "NodeChannelEnvironmentStopActorSpawnsResponse" });

export const nodeChannelEnvironmentPreviewGrantResponseSchema = z
  .object({
    code: z.string().min(1),
    expires_at: z.string(),
    public_port: z.number().int().min(1).max(65_535),
  })
  .meta({ ref: "NodeChannelEnvironmentPreviewGrantResponse" });

const nodeInflightStepSchema = z
  .object({
    actor_instance_id: z.string().nullable(),
    agent_mode_run_id: z.string().nullable(),
    model_ref: z.string().nullable(),
    organization_id: z.string(),
    phase: z.string().nullable(),
    started_at_ms: z.number().int(),
    step_id: z.string(),
    step_kind: z.string(),
    trigger_reason: z.string().nullable(),
    wake_group_id: z.string(),
  })
  .meta({ ref: "NodeInflightStep" });

const nodeInflightWakeSchema = z
  .object({
    actor_instance_id: z.string(),
    agent_mode_run_id: z.string().nullable(),
    current_step_id: z.string().nullable(),
    organization_id: z.string(),
    state: z.enum(["preparing", "lock_waiting", "llm_call", "between_steps"]),
    state_age_ms: z.number().int().nonnegative(),
    state_since_ms: z.number().int(),
    wake_group_id: z.string(),
    wake_kind: z.enum([
      "actor_wake",
      "consolidation_only_wake",
      "external_compact_seed",
    ]),
  })
  .meta({ ref: "NodeInflightWake" });

export const nodeInflightResponseSchema = z
  .object({
    node_id: z.string(),
    steps: z.array(nodeInflightStepSchema),
    wakes: z.array(nodeInflightWakeSchema),
  })
  .meta({ ref: "NodeInflightResponse" });
